/**
 * La cabecera de la plataforma: logo, las cinco aplicaciones, el tema y quién eres.
 *
 * **Por qué existe.** El portal tenía su barra con el logo y el perfil; el CRM, Marketing, el
 * catálogo y el calendario tenían, cada uno, una barra propia con un `←` y su nombre. Son cinco
 * pantallas del mismo producto y al pasar de una a otra desaparecían la marca, el acceso a las
 * demás y la cuenta con la que estás dentro: desde el CRM no había forma de llegar al catálogo sin
 * volver al portal, y nada decía con qué cuenta estabas trabajando.
 *
 * ⚠️ **Sólo en escritorio** (pedido del usuario, 21 oct 2026). En un teléfono la barra propia de
 * cada app ya ocupa dos renglones —186 px en el calendario, 117 en el CRM— y una tercera franja se
 * come la pantalla antes de mostrar un dato. El componente se monta igual en los dos anchos, y es
 * `cabecera.css` quien lo esconde bajo 900 px: montarlo condicionalmente dejaría la cabecera sin
 * aparecer al agrandar la ventana.
 *
 * ⚠️ **La altura se mide, no se escribe.** Hasta hoy `styles.css` tenía `73px` escrito a mano en
 * tres reglas y la barra medía **79** —la cuenta no incluía el alto real del chip del perfil—, así
 * que la lateral del portal se clavaba 6 px más arriba de lo que creía y su primera entrada quedaba
 * debajo de la cabecera translúcida. Acá la altura viaja en `--cab`, con un valor de respaldo en
 * CSS para el primer pintado y un `ResizeObserver` que la corrige en cuanto hay layout. Todo lo que
 * se cuelga debajo —`.pv-nav`, `.crm-nav`, `.ag-barra`— se posiciona contra esa variable y no puede
 * volver a desincronizarse.
 *
 * ⚠️ **El portal no usa `montarCabecera`: su barra está en `index.html`** y lleva, además de esto,
 * la campana, el Centro de Mensajes y el perfil, todos con listeners registrados en `app.js` al
 * evaluar el módulo. Lo que comparten las cinco son **las clases** (`.nav`, `.nav-logo`,
 * `.nav-right`, `.btn-icono`, `.user-badge`, `.btn-logout`), que viven en `portal/css/styles.css`
 * —hoja que las cinco páginas importan—, así que un cambio de estilo cae en las cinco a la vez. El
 * portal sí usa `fijarAltoCabecera` sobre su propio `<nav>`.
 */
import { montarBotonTema } from './tema.js';
// ⚠️ Importados y no escritos como ruta: Vite les pone hash, y una ruta a mano
// (`/assets/logo.png`) apuntaría a un nombre que el build ya no usa. Vienen de `portal/img/` porque
// es donde la marca ya vive y una segunda copia se desactualizaría sola; el panel de admin no
// importa este módulo, así que no se lleva el peso.
import logoOscuro from '../portal/img/logo.png';
import logoClaro from '../portal/img/logo-negro.webp';
import './cabecera.css';

/** El portal es el origen de todo: desde los subdominios hay que volver por URL absoluta. */
const PORTAL = 'https://app.deviant.cl/';

/**
 * Las cinco superficies, en el mismo orden en el que las ofrece el portal en «Inicio».
 *
 * El `href` es absoluto porque esta misma página se sirve en dos sitios —`crm.deviant.cl` y
 * `app.deviant.cl/crm.html`— y un relativo llevaría, desde el subdominio, a una ruta que ese vhost
 * no sirve. Las clases `ti-…` van literales: `fuentes.sh` recorta la fuente de iconos grepeándolas.
 */
const APPS = [
  { id: 'portal', nombre: 'Panel', icono: 'ti-bolt', href: PORTAL },
  { id: 'calendario', nombre: 'Calendario', icono: 'ti-calendar', href: `${PORTAL}agenda.html` },
  { id: 'crm', nombre: 'CRM', icono: 'ti-address-book', href: `${PORTAL}crm.html` },
  { id: 'marketing', nombre: 'Marketing', icono: 'ti-target-arrow', href: `${PORTAL}marketing.html` },
  { id: 'catalogo', nombre: 'Catálogo', icono: 'ti-tag', href: `${PORTAL}catalogo.html` },
];

/**
 * Mantener una variable CSS igual al alto real de una barra fija.
 *
 * Se exporta porque el portal, que no monta esta cabecera, necesita exactamente lo mismo sobre la
 * suya, y el calendario lo necesita sobre la segunda barra —la de las vistas y los filtros— para
 * calcular cuánto le queda a la grilla. El observador no se desconecta nunca a propósito: la barra
 * cambia de alto al cambiar de ancho la ventana y al aparecer el chip del perfil, y las dos cosas
 * pasan después del primer pintado.
 *
 * ⚠️ **El alto de una barra que envuelve no se puede escribir a mano**: la del calendario mide 235 px
 * en un teléfono de 390 —cuatro renglones— y el `calc()` que la daba por 150 le dejaba a la grilla
 * 85 px de más, que es de dónde salía el scroll de la página entera.
 */
export function fijarAltoCabecera(barra, variable = '--cab') {
  if (!barra) return;
  const medir = () => {
    // `display:none` bajo 900 px da 0, que es justo el valor correcto: ahí no hay nada debajo de lo
    // que colgarse y `calc(100dvh - 0px)` es el alto entero, como antes de que esto existiera.
    const alto = Math.round(barra.getBoundingClientRect().height);
    document.documentElement.style.setProperty(variable, `${alto}px`);
  };
  medir();
  if (window.ResizeObserver) new ResizeObserver(medir).observe(barra);
  else window.addEventListener('resize', medir);
}

/** La inicial del nombre, que es lo que se dibuja mientras no haya foto. */
const inicialDe = (nombre) => (nombre ? nombre.trim().charAt(0).toUpperCase() : 'D');

/**
 * Montar la cabecera al principio de `<body>`.
 *
 * @param {object} opciones
 * @param {string} opciones.app  Cuál de `APPS` es ésta: se marca y deja de ser un enlace.
 */
export function montarCabecera({ app }) {
  const barra = document.createElement('nav');
  barra.className = 'nav nav--comun';

  // El logo vuelve al panel. Dos `<img>` y no un `src` que cambia por JS, por lo mismo que en el
  // portal: el tema se aplica antes de pintar y un intercambio desde el bundle mostraría medio
  // segundo el logo blanco sobre fondo blanco. El que está oculto no lo anuncia ningún lector de
  // pantalla, así que los dos llevan el mismo `alt`.
  const logo = document.createElement('a');
  logo.className = 'nav-logo';
  logo.href = PORTAL;
  logo.title = 'Ir al panel';
  logo.setAttribute('aria-label', 'Ir al panel');
  logo.innerHTML = `
    <img class="logo-img logo-img--nav logo-oscuro" src="${logoOscuro}" alt="DEVIANT"
         width="1200" height="300" decoding="async">
    <img class="logo-img logo-img--nav logo-claro" src="${logoClaro}" alt="DEVIANT"
         width="1200" height="300" decoding="async">`;
  barra.appendChild(logo);

  const derecha = document.createElement('div');
  derecha.className = 'nav-right';
  barra.appendChild(derecha);

  // Las otras cuatro aplicaciones. La actual se dibuja marcada y como `<span>`: un enlace a la
  // página en la que ya estás recarga y pierde lo que haya a medio escribir.
  const apps = document.createElement('div');
  apps.className = 'cab-apps';
  for (const a of APPS) {
    const actual = a.id === app;
    const n = document.createElement(actual ? 'span' : 'a');
    n.className = `btn-icono${actual ? ' btn-icono--actual' : ''}`;
    n.title = a.nombre;
    if (actual) n.setAttribute('aria-current', 'page');
    else {
      n.href = a.href;
      n.setAttribute('aria-label', a.nombre);
    }
    n.innerHTML = `<i class="ti ${a.icono}" aria-hidden="true"></i>`;
    apps.appendChild(n);
  }
  derecha.appendChild(apps);

  // El tema. En escritorio vive acá para las cinco; cada app conserva el suyo en su propia barra
  // para el teléfono, donde esta cabecera no se dibuja (ver `cabecera.css`). Quedan dos botones
  // montados y eso es a propósito: el que sobra está escondido por CSS, y el ancho puede cambiar
  // sin recargar. Su panel flotante nunca se abre, así que no hay dos paneles compitiendo.
  const tema = document.createElement('div');
  derecha.appendChild(tema);
  montarBotonTema(tema);

  // Quién eres. Es un enlace al perfil del portal y no un menú: el editor de perfil vive en
  // `app.js`, y duplicarlo acá serían dos formularios que guardan lo mismo.
  const chip = document.createElement('a');
  chip.className = 'user-badge';
  chip.href = `${PORTAL}#perfil`;
  chip.title = 'Tu perfil';
  chip.innerHTML = '<span class="user-avatar" id="cab-avatar">D</span>'
    + '<span class="user-name" id="cab-nombre">—</span>';
  derecha.appendChild(chip);

  const salir = document.createElement('button');
  salir.type = 'button';
  salir.className = 'btn-logout';
  salir.id = 'cab-salir';
  salir.innerHTML = '<i class="ti ti-logout" aria-hidden="true"></i> Salir';
  derecha.appendChild(salir);
  salir.addEventListener('click', async () => {
    // Sin `apiFetch`: si la sesión ya está muerta, el 401 no tiene que impedir salir. Se va al
    // portal pase lo que pase, que es lo que el usuario pidió al apretar.
    try {
      await fetch(`${PORTAL.replace('app.', 'api.')}portal/logout`,
        { method: 'POST', credentials: 'include' });
    } catch { /* la sesión se cierra igual del lado del navegador */ }
    location.href = PORTAL;
  });

  document.body.prepend(barra);
  fijarAltoCabecera(barra);
  llenarCuenta();
  return barra;
}

/**
 * El nombre y la inicial, desde la sesión.
 *
 * Falla callado y deja el guión: una cabecera sin nombre es un detalle, y un toast de error cada vez
 * que se abre el CRM con la red lenta sería peor que el hueco. La llamada es la misma que ya hace el
 * portal al arrancar, así que no agrega un viaje nuevo a ninguna pantalla que ya la pida.
 */
async function llenarCuenta() {
  try {
    const r = await fetch('https://api.deviant.cl/portal/sesion', { credentials: 'include' });
    if (!r.ok) return;
    const { usuario } = await r.json();
    if (!usuario) return;
    const nombre = document.getElementById('cab-nombre');
    const avatar = document.getElementById('cab-avatar');
    if (nombre) nombre.textContent = usuario.nombre ?? '—';
    if (avatar) {
      avatar.textContent = inicialDe(usuario.nombre);
      // ⚠️ Con foto se **agrega** la imagen, nunca se pone `backgroundImage = 'none'` para quitarla:
      // el degradado de `.user-avatar` **es** un `background-image`, así que apagarlo deja el
      // círculo transparente y la inicial blanca invisible sobre fondo claro. Es el defecto que
      // tenía `updateAvatarDisplays()` en el portal.
      if (usuario.foto_perfil) {
        avatar.style.backgroundImage = `url(${usuario.foto_perfil})`;
        avatar.textContent = '';
      }
    }
  } catch { /* sin sesión legible, la cabecera se queda con el guión */ }
}
